// src/components/reservation/reservationFlow.logic.js
import { fromZonedTime, toZonedTime, formatInTimeZone } from "date-fns-tz";

/* ========================= tiny utils ========================= */
export function safeStr(v) {
  return (v || "").toString().trim();
}

export function digitsOnly(v) {
  return safeStr(v).replace(/[^\d]/g, "");
}

export function uniq(arr) {
  return Array.from(new Set((arr || []).filter(Boolean)));
}

export function normalizeGroupKey(name) {
  return safeStr(name).toLowerCase().replace(/\s+/g, " ");
}

function escapeRegExp(v) {
  return safeStr(v).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function carModelYear(car) {
  const raw = safeStr(car?.year);
  if (!raw) return "";

  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) return String(Math.trunc(n));
  return raw;
}

export function carModelYears(cars = []) {
  const years = Array.from(new Set((cars || []).map(carModelYear).filter(Boolean)));
  return years.sort((a, b) => {
    const an = Number(a);
    const bn = Number(b);
    if (Number.isFinite(an) && Number.isFinite(bn)) return bn - an;
    return safeStr(b).localeCompare(safeStr(a));
  });
}

export function formatCarNameWithYear(car, fallback = "Unnamed") {
  const name = safeStr(car?.name) || fallback;
  const year = carModelYear(car);
  if (!name || !year) return name;

  const yearPattern = new RegExp(`(^|[^0-9])${escapeRegExp(year)}([^0-9]|$)`);
  return yearPattern.test(name) ? name : `${name} (${year})`;
}

export function formatCarNameYearPlate(car, fallback = "Unnamed") {
  const name = formatCarNameWithYear(car, fallback);
  const plate = safeStr(car?.plate);
  return plate ? `${name} (${plate})` : name;
}

export function formatCarNameWithoutYear(car, fallback = "Unnamed") {
  const name = safeStr(car?.name) || fallback;
  const year = carModelYear(car);
  if (!name || !year) return name;

  const escapedYear = escapeRegExp(year);
  const cleanName = name
    .replace(new RegExp(`\\s*\\(\\s*${escapedYear}\\s*\\)`, "g"), " ")
    .replace(new RegExp(`(^|[\\s,/-])${escapedYear}(?=$|[\\s,/-])`, "g"), "$1")
    .replace(/\s*[-,/]\s*$/g, "")
    .replace(/^\s*[-,/]\s*/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();

  return cleanName || fallback;
}

export function formatCarNameWithoutYearPlate(car, fallback = "Unnamed") {
  const name = formatCarNameWithoutYear(car, fallback);
  const plate = safeStr(car?.plate);
  return plate ? `${name} (${plate})` : name;
}

/* ========================= timezone helpers ========================= */
export function timeZoneForCountryCode(countryCode) {
  const cc = safeStr(countryCode).toUpperCase();
  if (cc === "GE") return "Asia/Tbilisi";
  if (cc === "AL") return "Europe/Tirane";
  return "UTC";
}

// dtLocal is "YYYY-MM-DDTHH:mm" interpreted in tz
export function toISOInZone(dtLocal, tz) {
  const utcDate = fromZonedTime(dtLocal, tz);
  return utcDate.toISOString();
}

// returns "YYYY-MM-DDTHH:mm" representing "now" in tz
export function nowLocalInputInZone(tz) {
  const now = new Date();
  const zoned = toZonedTime(now, tz);

  const pad = (n) => String(n).padStart(2, "0");
  return (
    zoned.getFullYear() +
    "-" + pad(zoned.getMonth() + 1) +
    "-" + pad(zoned.getDate()) +
    "T" + pad(zoned.getHours()) +
    ":" + pad(zoned.getMinutes())
  );
}

// add hours to "local wall time" in tz and return local input string
export function addHoursLocalInputInZone(localInput, hours, tz) {
  const utc = fromZonedTime(localInput, tz);
  utc.setHours(utc.getHours() + hours);
  const zoned = toZonedTime(utc, tz);

  const pad = (n) => String(n).padStart(2, "0");
  return (
    zoned.getFullYear() +
    "-" + pad(zoned.getMonth() + 1) +
    "-" + pad(zoned.getDate()) +
    "T" + pad(zoned.getHours()) +
    ":" + pad(zoned.getMinutes())
  );
}

export function formatDateTimeNiceInZone(localInput, tz) {
  const utc = fromZonedTime(localInput, tz);
  return formatInTimeZone(utc, tz, "EEE, MMM dd, yyyy HH:mm");
}

export function formatDateTimeNice(localInput, tz) {
  if (!safeStr(localInput)) return "—";
  try {
    return formatDateTimeNiceInZone(localInput, tz);
  } catch {
    return safeStr(localInput);
  }
}

/* ========================= datetime picker helpers ========================= */
export function parseLocalDT(localValue) {
  if (!safeStr(localValue)) return { date: "", time: "" };
  const [d, t] = localValue.split("T");
  return { date: safeStr(d), time: safeStr(t) };
}

export function buildLocalDT(date, time) {
  const d = safeStr(date);
  const t = safeStr(time);
  if (!d || !t) return "";
  return `${d}T${t}`;
}

export function clampToMinInZone(localValue, minValue, tz) {
  if (!safeStr(minValue) || !safeStr(localValue)) return localValue;
  try {
    const a = fromZonedTime(localValue, tz).getTime();
    const b = fromZonedTime(minValue, tz).getTime();
    if (!Number.isFinite(a) || !Number.isFinite(b)) return localValue;
    return a < b ? minValue : localValue;
  } catch {
    return localValue;
  }
}

/* ========================= currencies / FX ========================= */
export const BACKEND_CURRENCY = "USD";

export const DISPLAY_CURRENCY_OPTIONS = [
  { code: "USD", label: "USD" },
  { code: "EUR", label: "EUR" },
  { code: "GBP", label: "GBP" },
  { code: "AED", label: "AED" },
  { code: "GEL", label: "GEL" },
  { code: "ALL", label: "LEK" }, // display "LEK", store ISO "ALL"
];

export const FX_BASE = "USD";
export const FX_SYMBOLS = ["USD", "EUR", "GBP", "AED", "GEL", "ALL"];

export function normalizeFxRates(ratesRaw) {
  const r = ratesRaw && typeof ratesRaw === "object" ? ratesRaw : {};
  const out = { USD: 1 };
  for (const code of FX_SYMBOLS) {
    if (code === "USD") continue;
    const v = Number(r?.[code]);
    if (Number.isFinite(v) && v > 0) out[code] = v;
  }
  return out;
}

// Convert using USD as pivot: from -> USD -> to
export function convertCurrency(amount, fromCode, toCode, fxRates) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return null;

  const from = safeStr(fromCode).toUpperCase() || "USD";
  const to = safeStr(toCode).toUpperCase() || "USD";
  const fx = fxRates || {};

  // to USD
  let usd = n;
  if (from !== "USD") {
    const rFrom = Number(fx[from]);
    if (!Number.isFinite(rFrom) || rFrom <= 0) return null;
    usd = n / rFrom;
  }

  // USD to target
  if (to === "USD") return usd;
  const rTo = Number(fx[to]);
  if (!Number.isFinite(rTo) || rTo <= 0) return null;
  return usd * rTo;
}

export function toUSD(amount, fromCode, fxRates) {
  const v = convertCurrency(amount, fromCode, "USD", fxRates);
  // fail-soft: treat as USD if FX missing
  return v == null ? Number(amount) : v;
}

export function formatMoney(amount, currency) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: Math.abs(n - Math.round(n)) >= 0.005 ? 2 : 0,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${n.toFixed(Math.abs(n - Math.round(n)) >= 0.005 ? 2 : 0)} ${currency}`;
  }
}

export const SITE_RESERVATION_DISCOUNT_RATE = 0.10;
export const SITE_RESERVATION_DISCOUNT_LABEL = "Reserve now and get an instant 10% off the rental amount";

function numOrNull(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function roundMoney(amount) {
  const n = Number(amount);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : null;
}

/* ========================= country / company helpers ========================= */
export function localCurrencyForCountryCode(countryCode) {
  const c = safeStr(countryCode).toUpperCase();
  if (c === "AL") return "EUR";
  if (c === "GE") return "USD";
  return "USD";
}

export function locationCodeFromCountry(country) {
  const c = safeStr(country);
  if (c.toLowerCase().startsWith("geo")) return "GE";
  if (c.toLowerCase().startsWith("alb")) return "AL";
  return "";
}

export function pickBestCompany(companies, country) {
  const byCountry = (companies || []).filter(
    (c) => safeStr(c.country).toLowerCase() === safeStr(country).toLowerCase()
  );
  if (!byCountry.length) return null;
  const haste = byCountry.find((c) => safeStr(c.name).toLowerCase().includes("haste"));
  return haste || byCountry[0];
}

/* ========================= rental math ========================= */
export const MIN_RENTAL_DAYS = 3;

export function calcRentalDays(startIso, endIso) {
  const s = new Date(startIso).getTime();
  const e = new Date(endIso).getTime();
  if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) return 0;
  const dayMs = 1000 * 60 * 60 * 24;
  return Math.max(1, Math.ceil((e - s) / dayMs));
}

export function overlaps(aStartIso, aEndIso, bStartIso, bEndIso) {
  const aS = new Date(aStartIso).getTime();
  const aE = new Date(aEndIso).getTime();
  const bS = new Date(bStartIso).getTime();
  const bE = new Date(bEndIso).getTime();
  return aS < bE && aE > bS;
}

/* ========================= delivery fees + areas ========================= */
export const GEO_DELIVERY_FEE_PER_LEG = 10; // USD
export const AL_DELIVERY_FEE_PER_LEG = 30; // USD
export const CROSS_BORDER_FEE_USD = 83; // per selected country (AL only)
export const CROSS_BORDER_ALLOWED_COUNTRIES = [
  "Kosovo",
  "Greece",
  "North Macedonia",
  "Montenegro",
];
const CROSS_BORDER_COUNTRY_BY_KEY = CROSS_BORDER_ALLOWED_COUNTRIES.reduce((acc, country) => {
  acc[normalizeCrossBorderCountryToken(country)] = country;
  return acc;
}, {});

function normalizeCrossBorderCountryToken(value) {
  return safeStr(value).toLowerCase().replace(/[\s_-]+/g, "");
}

export function normalizeCrossBorderCountries(value) {
  const raw = [];

  const add = (item) => {
    if (Array.isArray(item)) {
      item.forEach(add);
      return;
    }
    const s = safeStr(item);
    if (!s) return;
    if (s.includes(",")) {
      s.split(",").forEach(add);
      return;
    }
    raw.push(s);
  };

  add(value);

  const out = [];
  for (const item of raw) {
    const country = CROSS_BORDER_COUNTRY_BY_KEY[normalizeCrossBorderCountryToken(item)];
    if (country && !out.includes(country)) out.push(country);
  }
  return out;
}

export function crossBorderFeeUsdForExtras(extras, countryCode) {
  if (safeStr(countryCode).toUpperCase() !== "AL") return 0;
  const countries = normalizeExtras(extras).crossBorderCountries;
  return countries.length * CROSS_BORDER_FEE_USD;
}

export const TBILISI_AREAS = [
  "Vake", "Saburtalo", "Old Tbilisi", "Mtatsminda", "Didube",
  "Isani", "Samgori", "Gldani", "Nadzaladevi", "Dighomi",
  "Ortachala", "Avlabari",
  "Chugureti", "Vera", "Sololaki", "Rustaveli", "Marjanishvili",
  "Freedom Square", "Orbeliani", "Lisi Lake", "Turtle Lake",
  "Tbilisi Sea", "Varketili", "Vazisubani", "Navtlughi",
  "Digomi Massive", "Mukhiani", "Didi Dighomi", "Tskneti",
  "Kojori", "Lilo",
];

export const TIRANA_AREAS = [
  "Tirana Center", "Blloku", "Rruga e Kavajës", "Komuna e Parisit",
  "21 Dhjetori",
  "Skanderbeg Square", "Myslym Shyri", "Don Bosko", "Ali Demi",
  "Lapraka", "Kinostudio", "Astir", "Yzberisht", "Selitë",
  "Fresku", "Xhamlliku", "Tegu", "Sauku", "Kombinat",
];

export function computeDeliveryFee(countryCode, pickupMode, dropoffMode) {
  const isGE = safeStr(countryCode).toUpperCase() === "GE";
  const isAL = safeStr(countryCode).toUpperCase() === "AL";
  const perLeg = isGE ? GEO_DELIVERY_FEE_PER_LEG : isAL ? AL_DELIVERY_FEE_PER_LEG : 0;

  // these constants are USD amounts
  const currency = "USD";
  const pickupFee = pickupMode === "DELIVERY" ? perLeg : 0;
  const dropoffFee = dropoffMode === "DELIVERY" ? perLeg : 0;

  return { currency, pickupFee, dropoffFee, total: pickupFee + dropoffFee };
}

/* ========================= extras / insurance ========================= */
export const MAX_SEAT_EXTRA_COUNT = 3;

export const BASE_EXTRAS = {
  boosterSeat: false,
  boosterSeatCount: 0,
  infantSeat: false,
  infantSeatCount: 0,
  babySeat: false,
  babySeatCount: 0,
  snowChains: false,
  extraDriversCount: 0,
  crossBorder: false,
  crossBorderCountries: [],
};

export function normalizeSeatExtraCount(value, selectedFallback = false) {
  if (value == null || value === "") return selectedFallback ? 1 : 0;
  const n = Number(value);
  if (Number.isFinite(n)) return Math.max(0, Math.min(MAX_SEAT_EXTRA_COUNT, Math.floor(n)));
  return selectedFallback ? 1 : 0;
}

function readSeatExtraCount(extras, canonicalKey, fallbackSelected) {
  const e = extras || {};
  const keyNoSeat = canonicalKey.replace(/Seat$/, "");
  const candidates = [
    e[`${canonicalKey}Count`],
    e[`${canonicalKey}Quantity`],
    e[`${canonicalKey}Qty`],
    e[`${canonicalKey}s`],
    e[`${keyNoSeat}SeatsCount`],
    e[`${keyNoSeat}Seats`],
  ];

  for (const candidate of candidates) {
    if (candidate == null || candidate === "") continue;
    return normalizeSeatExtraCount(candidate, fallbackSelected);
  }

  return normalizeSeatExtraCount(null, fallbackSelected);
}

export function normalizeExtras(extras) {
  const e = extras || {};
  const boosterSeatCount = readSeatExtraCount(e, "boosterSeat", !!e.boosterSeat);
  const infantSeatCount = readSeatExtraCount(e, "infantSeat", !!e.infantSeat);
  const babySeatCount = readSeatExtraCount(e, "babySeat", !!(e.babySeat || e.babyCarSeat));
  const rawExtraDriversCount =
    e.extraDriversCount ??
    e.extraDriverCount ??
    (e.extraDriver === true ? 1 : 0);
  const n = Number(rawExtraDriversCount);
  const extraDriversCount = Number.isFinite(n) ? Math.max(0, Math.min(10, Math.floor(n))) : 0;
  const crossBorderCountries = normalizeCrossBorderCountries(
    e.crossBorderCountries ?? e.crossBorderDestinationCountries ?? e.crossBorderCountry
  );
  const crossBorder = !!e.crossBorder || crossBorderCountries.length > 0;
  return {
    boosterSeat: boosterSeatCount > 0,
    boosterSeatCount,
    infantSeat: infantSeatCount > 0,
    infantSeatCount,
    babySeat: babySeatCount > 0,
    babySeatCount,
    snowChains: !!e.snowChains,
    extraDriversCount,
    crossBorder,
    crossBorderCountries: crossBorder ? crossBorderCountries : [],
  };
}

export function quoteKey(carId, insuranceTier, extras) {
  const ex = normalizeExtras(extras);
  const tier = safeStr(insuranceTier) || "THIRD_PARTY";
  return `${carId}::${tier}::${JSON.stringify(ex)}`;
}

export function addonsMoneyCurrencyFor(countryCode, addonsPricing) {
  const cur = safeStr(addonsPricing?.currency).toUpperCase();
  if (cur) return cur;
  return localCurrencyForCountryCode(countryCode) || "USD";
}

export function normalizeBodyType(bodyType) {
  return safeStr(bodyType).toUpperCase().replace(/[\s_-]+/g, "");
}

export function georgiaInsurancePerDayForBodyType(bodyType) {
  switch (normalizeBodyType(bodyType)) {
    case "SEDAN":
    case "HATCHBACK":
      return 7;
    case "SUV":
      return 10;
    case "VAN":
    case "MINIVAN":
      return 15;
    default:
      return null;
  }
}

export function normalizeInsuranceTiers(raw, legacy1_5, legacy6p) {
  try {
    const arr = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (Array.isArray(arr) && arr.length) {
      return arr
        .map((t) => ({
          minDays: Number(t?.minDays),
          maxDays: t?.maxDays == null ? null : Number(t?.maxDays),
          perDay: Number(t?.perDay),
        }))
        .filter(
          (t) =>
            Number.isInteger(t.minDays) &&
            t.minDays >= 1 &&
            Number.isFinite(t.perDay) &&
            t.perDay >= 0
        )
        .sort((a, b) => a.minDays - b.minDays);
    }
  } catch {
    // ignore
  }

  const a = Number(legacy1_5);
  const b = Number(legacy6p);
  if (Number.isFinite(a) || Number.isFinite(b)) {
    return [
      { minDays: 1, maxDays: 5, perDay: Number.isFinite(a) ? a : 0 },
      { minDays: 6, maxDays: null, perDay: Number.isFinite(b) ? b : 0 },
    ];
  }

  return [];
}

export function pickInsurancePerDayFromTiers(tiers, days) {
  const d = Number(days);
  if (!Number.isFinite(d) || d <= 0) return 0;
  const t = (tiers || []).find((x) => d >= x.minDays && (x.maxDays == null || d <= x.maxDays));
  return t ? Number(t.perDay) : 0;
}

export function insurancePerDayForReservation(addonsPricing, days, countryCode, bodyType) {
  if (safeStr(countryCode).toUpperCase() === "GE") {
    const georgiaPerDay = georgiaInsurancePerDayForBodyType(bodyType);
    if (georgiaPerDay != null) return georgiaPerDay;
  }

  const tiers = normalizeInsuranceTiers(
    addonsPricing?.insuranceTiers,
    addonsPricing?.insurancePerDay_1_5,
    addonsPricing?.insurancePerDay_6_plus
  );
  return pickInsurancePerDayFromTiers(tiers, days);
}

export function normalizeBreakdown(breakdown) {
  if (!breakdown) return null;
  if (typeof breakdown === "object") return breakdown;
  if (typeof breakdown === "string" && breakdown.trim()) {
    try {
      return JSON.parse(breakdown);
    } catch {
      return null;
    }
  }
  return null;
}

export function getSiteDiscountSummary(breakdown) {
  const bd = normalizeBreakdown(breakdown);
  if (!bd) return null;

  const rentalAfter = numOrNull(bd?.baseTotal);
  const rentalBefore =
    numOrNull(bd?.baseTotalBeforeDiscount) ??
    numOrNull(bd?.discount?.rentalBeforeDiscount) ??
    rentalAfter;

  const discountAmount =
    numOrNull(bd?.discount?.amount) ??
    (rentalBefore != null && rentalAfter != null
      ? roundMoney(Math.max(0, rentalBefore - rentalAfter))
      : null);

  const totalAfter = numOrNull(bd?.total);
  const totalBefore =
    numOrNull(bd?.totalBeforeDiscount) ??
    numOrNull(bd?.discount?.totalBeforeDiscount) ??
    (totalAfter != null && discountAmount != null
      ? roundMoney(totalAfter + discountAmount)
      : totalAfter);

  const rate =
    numOrNull(bd?.discount?.rate) ??
    (rentalBefore != null && discountAmount != null && rentalBefore > 0
      ? discountAmount / rentalBefore
      : SITE_RESERVATION_DISCOUNT_RATE);

  const hasDiscount = discountAmount != null && discountAmount > 0;

  return {
    code: safeStr(bd?.discount?.code),
    label: safeStr(bd?.discount?.label) || SITE_RESERVATION_DISCOUNT_LABEL,
    appliesTo: safeStr(bd?.discount?.appliesTo) || "RENTAL_ONLY",
    rate: hasDiscount ? rate : 0,
    rentalBefore,
    rentalAfter,
    discountAmount: hasDiscount ? discountAmount : 0,
    totalBefore,
    totalAfter,
    hasDiscount,
  };
}

export function getBackendAddonsTotalFromBreakdown(breakdown) {
  const bd = normalizeBreakdown(breakdown);
  if (!bd) return null;

  const nested = bd?.addons?.total;
  if (Number.isFinite(Number(nested)) && Number(nested) >= 0) return Number(nested);

  const candidates = [
    bd.addonsTotal,
    bd.addOnsTotal,
    bd.extrasTotal,
    bd.insuranceTotal,
    bd.optionalTotal,
  ];

  for (const v of candidates) {
    const n = Number(v);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return null;
}

export function getCrossBorderLineTotalFromBreakdown(breakdown) {
  const bd = normalizeBreakdown(breakdown);
  const line = bd?.addons?.lines?.crossBorder || bd?.addons?.lines?.cross_border;
  if (!line || typeof line !== "object") return 0;

  const total = numOrNull(line.total);
  if (total != null && total >= 0) return total;

  const fee = numOrNull(line.fee);
  const quantity = numOrNull(line.quantity);
  if (fee != null && quantity != null && fee >= 0 && quantity >= 0) return fee * quantity;

  return 0;
}

export function getCrossBorderAddonsAdjustment(breakdown, extras, countryCode) {
  const expected = crossBorderFeeUsdForExtras(extras, countryCode);
  if (expected <= 0) return 0;

  const existing = getCrossBorderLineTotalFromBreakdown(breakdown);
  const diff = expected - existing;
  return diff > 0 ? Number(roundMoney(diff) ?? diff) : 0;
}

export function applyCrossBorderCountryPricingToBreakdown(breakdown, extras, countryCode) {
  const bd = normalizeBreakdown(breakdown);
  if (!bd || typeof bd !== "object") return bd;

  const expected = crossBorderFeeUsdForExtras(extras, countryCode);
  const adjustment = getCrossBorderAddonsAdjustment(bd, extras, countryCode);
  if (expected <= 0 || adjustment <= 0) return bd;

  const selectedCountries = normalizeExtras(extras).crossBorderCountries;
  const next = {
    ...bd,
    addons: {
      ...(bd.addons || {}),
      lines: {
        ...((bd.addons && bd.addons.lines) || {}),
      },
      extras: {
        ...((bd.addons && bd.addons.extras) || {}),
      },
    },
  };

  const existingLine = next.addons.lines.crossBorder || next.addons.lines.cross_border || {};
  next.addons.lines.crossBorder = {
    ...existingLine,
    fee: CROSS_BORDER_FEE_USD,
    currency: "USD",
    quantity: selectedCountries.length,
    countries: selectedCountries,
    total: expected,
    allowedCountries: [...CROSS_BORDER_ALLOWED_COUNTRIES],
  };
  delete next.addons.lines.cross_border;

  next.addons.extras = {
    ...next.addons.extras,
    crossBorder: true,
    crossBorderCountries: selectedCountries,
    crossBorderFeeUsd: CROSS_BORDER_FEE_USD,
    crossBorderFeeUsdPerCountry: CROSS_BORDER_FEE_USD,
    crossBorderFeeUsdTotal: expected,
    crossBorderAllowedCountries: [...CROSS_BORDER_ALLOWED_COUNTRIES],
  };

  const addonsTotal = numOrNull(next.addons.total);
  if (addonsTotal != null) next.addons.total = Number(roundMoney(addonsTotal + adjustment) ?? addonsTotal + adjustment);

  const total = numOrNull(next.total);
  if (total != null) next.total = Number(roundMoney(total + adjustment) ?? total + adjustment);

  const totalBeforeDiscount = numOrNull(next.totalBeforeDiscount);
  if (totalBeforeDiscount != null) {
    next.totalBeforeDiscount = Number(roundMoney(totalBeforeDiscount + adjustment) ?? totalBeforeDiscount + adjustment);
  }

  if (next.discount && typeof next.discount === "object") {
    const discountTotalAfter = numOrNull(next.discount.totalAfterDiscount);
    const discountTotalBefore = numOrNull(next.discount.totalBeforeDiscount);
    if (discountTotalAfter != null) {
      next.discount.totalAfterDiscount = Number(roundMoney(discountTotalAfter + adjustment) ?? discountTotalAfter + adjustment);
    }
    if (discountTotalBefore != null) {
      next.discount.totalBeforeDiscount = Number(roundMoney(discountTotalBefore + adjustment) ?? discountTotalBefore + adjustment);
    }
  }

  return next;
}

export function calcClientAddonsTotal(addonsPricing, insuranceTier, extrasObj, days, context = {}) {
  const d = Number(days);
  if (!Number.isFinite(d) || d <= 0) return 0;

  const ex = normalizeExtras(extrasObj);
  const ap = addonsPricing || {};

  const perDay = (key) => {
    const n = Number(ap?.[key]);
    return Number.isFinite(n) ? n : 0;
  };

  let total = 0;
  const insurancePerDay = insurancePerDayForReservation(
    ap,
    d,
    context?.countryCode,
    context?.bodyType
  );

  if ((safeStr(insuranceTier) || "THIRD_PARTY") === "FULL") {
    total += insurancePerDay * d;
  }

  if (ex.boosterSeatCount > 0) total += perDay("boosterSeatPerDay") * d * ex.boosterSeatCount;
  if (ex.infantSeatCount > 0) total += perDay("infantSeatPerDay") * d * ex.infantSeatCount;
  if (ex.babySeatCount > 0) total += perDay("babySeatPerDay") * d * ex.babySeatCount;
  if (ex.snowChains) total += perDay("snowChainsPerDay") * d;

  const extraDriverPerDay = perDay("extraDriverPerDay");
  if (ex.extraDriversCount > 0 && extraDriverPerDay > 0) {
    total += extraDriverPerDay * d * ex.extraDriversCount;
  }

  return Math.max(0, Math.round(total));
}

/* ========================= deposits ========================= */
export function calcPolicyDeposit(locationCode, category) {
  const loc = safeStr(locationCode).toUpperCase();
  const cat = safeStr(category).toUpperCase();

  if (loc === "GE") return { amount: 400, currency: "USD" };

  if (loc === "AL") {
    let amount = null;
    switch (cat) {
      case "ECONOMY": amount = 600; break;
      case "COMPACT":
      case "COMPACT_ELITE": amount = 800; break;
      case "INTERMEDIATE":
      case "INTERMEDIATE_ELITE":
      case "INTERMEDIATE_SUV":
      case "MINIVAN":
      case "COMPACT_SUV_CFAR": amount = 1000; break;
      case "SUV_ELITE": amount = 1200; break;
      default: amount = null;
    }
    return amount == null ? null : { amount, currency: "EUR" };
  }

  return null;
}

export function calcDepositAmount(countryCode, category, insuranceTier) {
  const cc = safeStr(countryCode).toUpperCase();
  if (cc === "AL" && safeStr(insuranceTier) === "FULL") return 0; // AL rule: FULL => no deposit
  const d = calcPolicyDeposit(countryCode, category);
  return d && Number.isFinite(Number(d.amount)) ? Number(d.amount) : null;
}

/* ========================= quote totals (for sorting groups) ========================= */
export function quoteTotalForVariantQuote(
  q,
  addonsPricing,
  insuranceTier,
  extras,
  rentalDays,
  deliveryFeesUsd,
  countryCode,
  fxRates,
  bodyType
) {
  if (!q) return Infinity;
  if (q.manualQuote) return Infinity;

  const breakdownAddons = getBackendAddonsTotalFromBreakdown(q?.breakdown);
  const backendCrossBorderAdjustment = getCrossBorderAddonsAdjustment(q?.breakdown, extras, countryCode);

  const addons =
    Number.isFinite(Number(breakdownAddons))
      ? Number(breakdownAddons) + Number(backendCrossBorderAdjustment || 0)
      : (() => {
          const estimate = calcClientAddonsTotal(
            addonsPricing,
            insuranceTier,
            extras,
            rentalDays,
            { countryCode, bodyType }
          );
          const apCur = addonsMoneyCurrencyFor(countryCode, addonsPricing);
          const baseUsd = toUSD(estimate, apCur, fxRates);
          const crossBorderUsd = crossBorderFeeUsdForExtras(extras, countryCode);
          return Number(baseUsd || 0) + crossBorderUsd;
        })();

  const assumeBackendIncludesAddons = Number.isFinite(Number(breakdownAddons));
  const baseTotal = Number.isFinite(Number(q.total)) ? Number(q.total) : Infinity;

  const totalUsd = assumeBackendIncludesAddons
    ? baseTotal + Number(backendCrossBorderAdjustment || 0)
    : baseTotal + Number(addons || 0);
  return Number.isFinite(totalUsd) ? totalUsd + Number(deliveryFeesUsd || 0) : Infinity;
}

export function groupCheapestTotal(
  variants,
  quotesByKey,
  insuranceTier,
  extras,
  addonsPricing,
  rentalDays,
  deliveryFeesUsd,
  countryCode,
  fxRates
) {
  let best = Infinity;
  for (const v of variants || []) {
    const k = quoteKey(v.id, insuranceTier, extras);
    const q = quotesByKey?.[k]?.quote;
    const t = quoteTotalForVariantQuote(
      q,
      addonsPricing,
      insuranceTier,
      extras,
      rentalDays,
      deliveryFeesUsd,
      countryCode,
      fxRates,
      v?.bodyType
    );
    if (t < best) best = t;
  }
  return best;
}
